import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import BookCard from '@/components/BookCard';
import { apiClient } from '@/services/api';
import { Category, Book } from '@/types';

const CategoryView: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const [category, setCategory] = useState<Category | null>(null);
  const [books, setBooks] = useState<Book[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!categoryId) {
        setError('Category not found');
        return;
      }

      try {
        const [categoriesData, booksData] = await Promise.all([
          apiClient.getCategories(),
          apiClient.getBooks(categoryId),
        ]);

        const foundCategory = categoriesData.find((c) => c.id === categoryId);
        if (!foundCategory) {
          setError('Category not found');
          return;
        }

        setCategory(foundCategory);
        setBooks(booksData);
      } catch (err) {
        console.error('Error loading category:', err);
        setError('Failed to load category');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [categoryId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-[var(--text-secondary)]">Loading category...</p>
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="text-center py-12">
        <p className="text-[var(--text-secondary)] mb-4">{error}</p>
        <Link to="/" className="text-[var(--accent-primary)] hover:text-[var(--accent-hover)]">
          Back to home
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-8 py-12 space-y-8">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-[var(--accent-primary)] hover:text-[var(--accent-hover)] transition-colors"
      >
        <span>←</span>
        Back
      </button>

      {/* Category header */}
      <div className="mb-8">
        <div className="text-5xl mb-4">{category.icon}</div>
        <h1 className="text-4xl font-serif font-bold text-[var(--text-primary)] mb-3">
          {category.name}
        </h1>
        <p className="text-lg text-[var(--text-secondary)] max-w-2xl">
          {category.description}
        </p>
      </div>

      {/* Books grid */}
      <section>
        <h2 className="text-2xl font-serif font-semibold text-[var(--text-primary)] mb-6">
          {books.length} {books.length === 1 ? 'Book' : 'Books'}
        </h2>
        {books.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {books.map((book) => (
              <BookCard key={book.id} book={book} />
            ))}
          </div>
        ) : (
          <p className="text-[var(--text-secondary)] text-center py-12">
            No books available in this category yet.
          </p>
        )}
      </section>
    </div>
  );
};

export default CategoryView;
